import { Brain, Sparkles, MessageSquare, Target, Zap, BarChart3, Globe, ShieldCheck, Rocket, Bot } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { PageHero, HeroGlassCard } from "@/components/shared/PageHero";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { GlowCard } from "@/components/shared/GlowCard";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const AICampaignCreator = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const ar = language === "ar";
  const { ref: r1 } = useRevealOnScroll<HTMLDivElement>();
  const { ref: r2 } = useRevealOnScroll<HTMLDivElement>();
  const { ref: r3 } = useRevealOnScroll<HTMLDivElement>();

  type Accent = "blue" | "violet" | "emerald" | "amber";

  const features: Array<{ icon: typeof Brain; title: string; desc: string; accent: Accent }> = [
    {
      icon: MessageSquare,
      title: ar ? "محادثة ذكية" : "Smart conversation",
      desc: ar
        ? "أخبر المساعد بهدفك وميزانيتك، وسيقترح خطة كاملة مع المواقع المناسبة."
        : "Tell the assistant your goal and budget, and it proposes a full plan with the right locations.",
      accent: "blue",
    },
    {
      icon: Target,
      title: ar ? "استهداف دقيق" : "Precise targeting",
      desc: ar
        ? "يختار الذكاء الاصطناعي المدن والشاشات بناءً على الجمهور والوصول والعائد المتوقع."
        : "AI picks cities and faces based on audience, reach and expected ROI.",
      accent: "violet",
    },
    {
      icon: Globe,
      title: ar ? "تغطية وطنية" : "Nationwide coverage",
      desc: ar
        ? "اقتراحات تشمل أكثر من مدينة عند طلب تغطية وطنية، مع توازن في التوزيع."
        : "Suggests locations across multiple cities for nationwide prompts with balanced distribution.",
      accent: "emerald",
    },
    {
      icon: Zap,
      title: ar ? "إطلاق سريع" : "Launch in minutes",
      desc: ar
        ? "خطة جاهزة للحجز خلال دقائق بدلاً من ساعات من التخطيط اليدوي."
        : "A bookable plan in minutes instead of hours of manual planning.",
      accent: "amber",
    },
    {
      icon: BarChart3,
      title: ar ? "تحسين مستمر" : "Continuous optimization",
      desc: ar
        ? "توصيات مدعومة بالبيانات لتحسين الميزانية والتوزيع قبل الإطلاق."
        : "Data-driven recommendations to optimize budget and distribution before launch.",
      accent: "blue",
    },
    {
      icon: ShieldCheck,
      title: ar ? "بدون اقتراحات فارغة" : "Never zero results",
      desc: ar
        ? "ضمانات داخلية تمنع المخطط من اقتراح صفر مواقع، مع بدائل اقتصادية دائمًا."
        : "Built-in guards ensure the planner never returns zero locations; budget fallbacks always apply.",
      accent: "violet",
    },
  ];

  const steps: Array<{ icon: typeof Bot; title: string; desc: string }> = [
    {
      icon: MessageSquare,
      title: ar ? "صف حملتك" : "Describe your campaign",
      desc: ar
        ? "اكتب هدفك بلغة طبيعية بالعربية أو الإنجليزية: المدينة، المدة، الميزانية."
        : "Write your goal in natural language (Arabic or English): cities, duration, budget.",
    },
    {
      icon: Brain,
      title: ar ? "اقتراح الخطة" : "AI proposes a plan",
      desc: ar
        ? "يقترح المساعد قائمة شاشات وأوقات بث وميزانية موزعة بذكاء."
        : "The assistant proposes a face list, schedule and a smartly distributed budget.",
    },
    {
      icon: Rocket,
      title: ar ? "راجع وأطلق" : "Review and launch",
      desc: ar
        ? "عدّل الخطة بنقرة واحدة ثم أطلق الحملة مباشرة من نفس الشاشة."
        : "Tweak the plan in one click, then launch the campaign right from the same screen.",
    },
  ];

  return (
    <div className="relative min-h-screen">
      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHero
          eyebrow={ar ? "مدعوم بالذكاء الاصطناعي" : "AI-powered planning"}
          title={ar ? "منشئ الحملات" : "AI Campaign"}
          accent={ar ? "بالذكاء الاصطناعي" : "Creator"}
          subtitle={
            ar
              ? "صمّم وأطلق حملتك الإعلانية الخارجية في دقائق. صف هدفك، واترك الذكاء الاصطناعي يبني الخطة المثالية لك."
              : "Design and launch your DOOH campaign in minutes. Describe your goal and let AI build the perfect plan for you."
          }
          ctas={[
            {
              label: ar ? "ابدأ الآن" : "Start now",
              onClick: () => navigate(user ? "/campaign/ai-chat" : "/auth"),
            },
            {
              label: ar ? "إنشاء يدوي" : "Manual creation",
              to: "/campaign",
              variant: "secondary",
            },
          ]}
          rightSlot={
            <HeroGlassCard>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-semibold text-white/70 uppercase tracking-widest">
                  {ar ? "محادثة مباشرة" : "Live chat"}
                </div>
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[9px] font-semibold">
                  AI
                </span>
              </div>
              <div className="space-y-3">
                <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-xs text-white/80">
                  {ar
                    ? "أريد حملة في الرياض وجدة لمدة أسبوعين بميزانية 80,000 ريال."
                    : "I want a 2-week campaign in Riyadh and Jeddah with an 80,000 SAR budget."}
                </div>
                <div className="rounded-xl bg-primary/15 border border-primary/30 p-3 text-xs text-white/90">
                  <div className="flex items-center gap-2 mb-1.5 text-primary">
                    <Sparkles className="w-3 h-3" />
                    <span className="font-semibold">{ar ? "الخطة المقترحة" : "Suggested plan"}</span>
                  </div>
                  {ar
                    ? "12 شاشة عالية التدفق · 14 يومًا · توزيع 60/40 بين الرياض وجدة."
                    : "12 high-traffic faces · 14 days · 60/40 split between Riyadh and Jeddah."}
                </div>
              </div>
            </HeroGlassCard>
          }
        />

        {/* Features */}
        <section ref={r1} className="reveal-on-scroll pb-16 md:pb-24">
          <SectionHeader
            eyebrow={ar ? "الميزات" : "Features"}
            title={ar ? "تخطيط أذكى وأسرع" : "Smarter, faster planning"}
            subtitle={ar
              ? "كل ما تحتاجه لبناء حملات DOOH ناجحة، مدمجًا في محادثة واحدة."
              : "Everything you need to build successful DOOH campaigns, packed into a single conversation."}
          />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <GlowCard key={i} icon={f.icon} accent={f.accent} title={f.title} description={f.desc} />
            ))}
          </div>
        </section>

        {/* How it works */}
        <section ref={r2} className="reveal-on-scroll pb-16 md:pb-24">
          <SectionHeader
            title={ar ? "كيف يعمل" : "How it works"}
            subtitle={ar ? "ثلاث خطوات بسيطة من الفكرة إلى الإطلاق." : "Three simple steps from idea to launch."}
          />
          <div className="grid gap-6 md:grid-cols-3">
            {steps.map((s, i) => (
              <div key={i} className="relative bg-card border border-border/60 rounded-2xl p-6 shadow-card">
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary-light text-primary-foreground flex items-center justify-center text-sm font-bold shadow-lg shadow-primary/30">
                  {i + 1}
                </div>
                <s.icon className="w-8 h-8 text-primary mb-3" />
                <h3 className="text-lg font-bold text-foreground mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section ref={r3} className="reveal-on-scroll pb-16 md:pb-24">
          <div className="bg-gradient-to-br from-[hsl(220_60%_10%)] to-[hsl(220_55%_14%)] border border-white/10 rounded-2xl p-8 md:p-12 text-center shadow-2xl shadow-primary/20">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 border border-primary/30 text-primary text-[11px] font-semibold tracking-widest uppercase mb-4">
              <Bot className="w-3 h-3" />
              {ar ? "جاهز للبدء؟" : "Ready to launch?"}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
              {ar ? "ابدأ حملتك الذكية الآن" : "Start your smart campaign now"}
            </h2>
            <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed">
              {ar
                ? "اكتب هدفك، ودع الذكاء الاصطناعي يقوم بالباقي. أنت دائمًا متحكم في القرار النهائي."
                : "Type your goal and let AI do the rest. You stay in control of the final decision."}
            </p>
            <button
              onClick={() => navigate(user ? "/campaign/ai-chat" : "/auth")}
              className="h-12 px-7 rounded-full font-semibold text-sm md:text-base inline-flex items-center gap-2 bg-gradient-to-r from-primary to-primary-light text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 transition-all"
            >
              <Sparkles className="w-4 h-4" />
              {ar ? "افتح المساعد الذكي" : "Open the AI assistant"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AICampaignCreator;
